import {
  FieldTypeDTO,
} from 'dtos';
import startCase from 'lodash/startCase';
import isEmpty from 'lodash/isEmpty';
import {
  isEmptyString,
} from '@populateui/utils';
import {
  PdfTemplateFieldDefinitionGroup,
  PdfTemplateFieldDefinition,
  PdfTemplateFieldType,
} from 'views/EMR/Settings/PdfTemplateBuilder/types';

export function normalizeFieldTypeName(fieldType?: string | null): PdfTemplateFieldType {
  if (fieldType === 'radio') {
    return 'radioGroup';
  }

  return fieldType ?? '';
}

function normalizeFieldTypeDefinition(
  fieldType: FieldTypeDTO,
): PdfTemplateFieldDefinition | null {
  const normalizedType = normalizeFieldTypeName(fieldType.type);
  if (isEmptyString(normalizedType)) {
    return null;
  }

  return {
    type: normalizedType,
    label: fieldType.name ?? startCase(normalizedType),
    description: 'Form builder field',
    grouping: fieldType.grouping ?? null,
  };
}

export function getFieldTypeDefinitions(
  fieldTypes?: FieldTypeDTO[] | null,
): PdfTemplateFieldDefinition[] {
  const definitions = fieldTypes?.reduce<PdfTemplateFieldDefinition[]>((acc, fieldType) => {
    const definition = normalizeFieldTypeDefinition(fieldType);

    if (definition && !acc.some((item) => item.type === definition.type)) {
      acc.push(definition);
    }

    return acc;
  }, []) ?? [];

  return definitions;
}

export function getGroupedFieldTypeDefinitions(
  fieldTypes?: FieldTypeDTO[] | null,
): PdfTemplateFieldDefinitionGroup[] {
  const definitions = getFieldTypeDefinitions(fieldTypes);
  if (isEmpty(definitions)) {
    return [];
  }

  const groups = definitions.reduce<PdfTemplateFieldDefinitionGroup[]>((acc, definition) => {
    const grouping = definition.grouping ?? null;
    const existingGroup = acc.find((group) => group.grouping === grouping);

    if (existingGroup) {
      existingGroup.definitions.push(definition);
      return acc;
    }

    acc.push({
      grouping,
      label: grouping ?? 'Ungrouped',
      definitions: [definition],
    });

    return acc;
  }, []);

  if (!groups.some((group) => group.grouping)) {
    return [{
      grouping: null,
      label: 'Ungrouped',
      definitions,
    }];
  }

  return groups;
}

export function getDefaultFieldType(
  fieldTypes?: FieldTypeDTO[] | null,
): PdfTemplateFieldType {
  return getFieldTypeDefinitions(fieldTypes)[0]?.type ?? '';
}

const SIGNATURE_FAMILY_FIELD_TYPES = new Set<PdfTemplateFieldType>([
  'signature',
  'initial',
  'signatureDate',
  'signatureTimestamp',
]);

export function isSignatureFamilyFieldType(fieldType?: string | null): boolean {
  return SIGNATURE_FAMILY_FIELD_TYPES.has(normalizeFieldTypeName(fieldType));
}

export function canToggleFieldOptional(fieldType?: string | null): boolean {
  return !isSignatureFamilyFieldType(fieldType);
}

const ALIAS_BLOCKED_FIELD_TYPES = new Set<PdfTemplateFieldType>(['checkbox', 'radioGroup', 'checkboxGroup']);

export const FIELD_TYPES_WITHOUT_ALIGNMENT: PdfTemplateFieldType[] = ['checkbox', 'radioGroup', 'checkboxGroup'];

export function fieldSupportsAlignment(fieldType?: string | null): boolean {
  return !FIELD_TYPES_WITHOUT_ALIGNMENT.includes(normalizeFieldTypeName(fieldType));
}

export function canSetAutoFillAlias(fieldType?: string | null): boolean {
  return !ALIAS_BLOCKED_FIELD_TYPES.has(normalizeFieldTypeName(fieldType));
}
